import React from "react";
import { useNavigate } from "react-router-dom";

const DataRetention = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f5f7fa]" data-testid="data-retention-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-10">
          <button onClick={() => navigate(-1)} className="text-sm text-[#4a9eff] hover:underline mb-4 inline-block" data-testid="data-retention-back-btn">
            &larr; Back
          </button>
          <h1 className="text-3xl font-bold text-[#1e3a5f] tracking-tight">Data Retention Policy</h1>
          <p className="text-sm text-[#5a6577] mt-2">Effective Date: March 30, 2026</p>
        </div>

        <div className="bg-white rounded-xl border border-[#e4e8ee] p-8 space-y-8 text-[#3d4f63] text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">Overview</h2>
            <p>Opturna collects and retains user data only for the limited purposes necessary to operate its financial planning and business intelligence services. When a user connects a financial account through Plaid, Opturna receives selected account and transaction information through Plaid's secure infrastructure. Opturna does not collect or store a user's online banking username, password, or security challenge responses.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">Data We May Retain</h2>
            <ul className="list-disc ml-6 space-y-2">
              <li>Basic account profile information, such as name, email address, business name, and plan details.</li>
              <li>Connected account metadata, such as institution name, account type, masked account identifier, and Plaid item metadata.</li>
              <li>Financial data needed to provide the service, including balances, transactions, and derived categorizations.</li>
              <li>Consent records, security logs, deletion requests, and support interactions.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">Retention Schedule</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-[#e4e8ee] rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-[#f5f7fa]">
                    <th className="text-left p-3 font-semibold text-[#1e3a5f] border-b border-[#e4e8ee]">Data Category</th>
                    <th className="text-left p-3 font-semibold text-[#1e3a5f] border-b border-[#e4e8ee]">Retention Period</th>
                    <th className="text-left p-3 font-semibold text-[#1e3a5f] border-b border-[#e4e8ee]">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#e4e8ee]">
                    <td className="p-3">Active account profile and service data</td>
                    <td className="p-3">While account remains active</td>
                    <td className="p-3 text-xs text-[#5a6577]">Used to provide product functionality.</td>
                  </tr>
                  <tr className="border-b border-[#e4e8ee] bg-[#fafbfc]">
                    <td className="p-3">Transaction and balance data</td>
                    <td className="p-3">Up to 24 months</td>
                    <td className="p-3 text-xs text-[#5a6577]">Retain only what is necessary for forecasting, reporting, and tax planning features.</td>
                  </tr>
                  <tr className="border-b border-[#e4e8ee]">
                    <td className="p-3">Consent logs and security logs</td>
                    <td className="p-3">Up to 36 months</td>
                    <td className="p-3 text-xs text-[#5a6577]">Helps document authorization, fraud prevention, and audit history.</td>
                  </tr>
                  <tr className="border-b border-[#e4e8ee] bg-[#fafbfc]">
                    <td className="p-3">Deletion requests and support records</td>
                    <td className="p-3">Up to 24 months after resolution</td>
                    <td className="p-3 text-xs text-[#5a6577]">Used to document compliance actions.</td>
                  </tr>
                  <tr>
                    <td className="p-3">Backup copies</td>
                    <td className="p-3">Purged within 60 days of deletion request</td>
                    <td className="p-3 text-xs text-[#5a6577]">Backups are not used to restore deleted accounts except for disaster recovery.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">Deletion and De-identification</h2>
            <p className="mb-3">Users may request deletion of their Opturna account at any time from within account settings or by emailing <a href="mailto:admin@opturnatech.com" className="text-[#4a9eff] hover:underline">admin@opturnatech.com</a>. Once a deletion request is initiated, Opturna will:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Revoke or sever active Plaid-linked connections associated with the account.</li>
              <li>Queue user profile data and linked service data for deletion.</li>
              <li>Complete production-data deletion within 30 days unless a shorter period is operationally available.</li>
              <li>Purge residual backup copies on a rolling basis within 60 days.</li>
              <li>Retain only the minimum records needed for legal compliance, fraud prevention, or dispute resolution.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">User Rights</h2>
            <p>Users may request access, correction, deletion, or disconnection of linked account data as described in the <a href="/privacy" className="text-[#4a9eff] hover:underline">Privacy Policy</a> and product settings.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">Legal Retention Exceptions</h2>
            <p>Opturna may retain limited information where required to comply with law, resolve disputes, enforce agreements, detect abuse, or complete security investigations. Any such retained information will be limited to what is necessary for the purpose.</p>
          </section>
        </div>

        <p className="text-center text-xs text-[#8b95a5] mt-8">Opturna Tech Inc. All rights reserved.</p>
      </div>
    </div>
  );
};

export default DataRetention;
